// Collection product filters: handled in sections (e.g. custom-collection.liquid) on
// .collection-product-item — avoid duplicating listeners on .custom-product-card here,
// which broke visibility (button data-type is lowercased; card data-type matches Shopify product.type casing).

// GSAP Slider functionality
(function() {
  function initBasicGSAPSlider() {
    if (typeof gsap === 'undefined' || typeof Draggable === 'undefined' || typeof InertiaPlugin === 'undefined') {
      return;
    }

    gsap.registerPlugin(Draggable, InertiaPlugin);

    const sliders = document.querySelectorAll('[data-gsap-slider-init]');
    
    sliders.forEach((root, sliderIndex) => {
      // Skip if already initialized (unless we're reinitializing)
      if (root._sliderInitialized && !root._needsReinit) {
        return;
      }
      
      // Clean up old instance
      if (root._sliderDraggable) {
        root._sliderDraggable.kill();
        root._sliderDraggable = null;
      }
      
      // Remove old control handlers
      if (root._controlHandlers) {
        root._controlHandlers.forEach(({btn, handler}) => {
          btn.removeEventListener('click', handler);
        });
        root._controlHandlers = null;
      }

      // Scope all queries to this specific root element
      const collection = root.querySelector('[data-gsap-slider-collection]');
      const track      = root.querySelector('[data-gsap-slider-list]');
      const items      = Array.from(root.querySelectorAll('[data-gsap-slider-item]'));
      const controlsContainer = root.querySelector('[data-gsap-slider-controls]');
      const controls   = controlsContainer ? Array.from(controlsContainer.querySelectorAll('[data-gsap-slider-control]')) : [];

      if (!collection || !track || items.length === 0) {
        console.warn(`GSAP Slider ${sliderIndex}: Missing required elements`);
        return;
      }

      // Inject aria attributes
      root.setAttribute('role','region');
      root.setAttribute('aria-roledescription','carousel');
      root.setAttribute('aria-label','Slider');
      collection.setAttribute('role','group');
      collection.setAttribute('aria-roledescription','Slides List');
      collection.setAttribute('aria-label','Slides');
      items.forEach((slide,i) => {
        slide.setAttribute('role','group');
        slide.setAttribute('aria-roledescription','Slide');
        slide.setAttribute('aria-label',`Slide ${i+1} of ${items.length}`);
        slide.setAttribute('aria-hidden','true');
        slide.setAttribute('aria-selected','false');
        slide.setAttribute('tabindex','-1');
      });
      controls.forEach(btn => {
        const dir = btn.getAttribute('data-gsap-slider-control');
        btn.setAttribute('role','button');
        btn.setAttribute('aria-label', dir==='prev' ? 'Previous Slide' : 'Next Slide');
        btn.disabled = true;
        btn.setAttribute('aria-disabled','true');
      });

      // Determine if slider runs
      const styles      = getComputedStyle(root);
      const statusVar   = styles.getPropertyValue('--slider-status').trim();
      let   spvVar      = parseFloat(styles.getPropertyValue('--slider-spv'));
      const rect        = items[0].getBoundingClientRect();
      const marginRight = parseFloat(getComputedStyle(items[0]).marginRight);
      const slideW      = rect.width + marginRight;
      if (isNaN(spvVar)) {
        spvVar = collection.clientWidth / slideW;
      }
      const spv           = Math.max(1, Math.min(spvVar, items.length));
      const sliderEnabled = statusVar==='on' && spv < items.length;
      root.setAttribute('data-gsap-slider-status', sliderEnabled ? 'active' : 'not-active');

      if (!sliderEnabled) {
        // Teardown when disabled
        track.removeAttribute('style');
        track.onmouseenter = null;
        track.onmouseleave = null;
        track.removeAttribute('data-gsap-slider-list-status');
        root.removeAttribute('role');
        root.removeAttribute('aria-roledescription');
        root.removeAttribute('aria-label');
        collection.removeAttribute('role');
        collection.removeAttribute('aria-roledescription');
        collection.removeAttribute('aria-label');
        items.forEach(slide => {
          slide.removeAttribute('role');
          slide.removeAttribute('aria-roledescription');
          slide.removeAttribute('aria-label');
          slide.removeAttribute('aria-hidden');
          slide.removeAttribute('aria-selected');
          slide.removeAttribute('tabindex');
          slide.removeAttribute('data-gsap-slider-item-status');
        });
        controls.forEach(btn => {
          btn.disabled = false;
          btn.removeAttribute('role');
          btn.removeAttribute('aria-label');
          btn.removeAttribute('aria-disabled');
          btn.removeAttribute('data-gsap-slider-control-status');
        });
        return;
      }

      // Track hover state
      track.onmouseenter = () => {
        track.setAttribute('data-gsap-slider-list-status','grab');
      };
      track.onmouseleave = () => {
        track.removeAttribute('data-gsap-slider-list-status');
      };

      // Calculate bounds and snap points
      const vw        = collection.clientWidth;
      const tw        = track.scrollWidth;
      const maxScroll = Math.max(tw - vw, 0);
      const minX      = -maxScroll;
      const maxX      = 0;
      const maxIndex  = maxScroll / slideW;
      const full      = Math.floor(maxIndex);
      const snapPoints = [];
      for (let i = 0; i <= full; i++) {
        snapPoints.push(-i * slideW);
      }
      if (full < maxIndex) {
        snapPoints.push(-maxIndex * slideW);
      }

      let activeIndex    = 0;
      const setX         = gsap.quickSetter(track,'x','px');
      let collectionRect = collection.getBoundingClientRect();
      const progressBar  = root.querySelector('.gsap-slider__progress-bar');
      
      // Calculate progress based on products
      const totalProducts = items.length;
      const visibleProducts = Math.min(spv, totalProducts);
      const productPercent = totalProducts > 0 ? 100 / totalProducts : 0;
      const initialProgress = productPercent * visibleProducts;

      function updateProgress() {
        if (progressBar && totalProducts > 0) {
          // Calculate progress: initial visible products + (activeIndex * one product's worth)
          // activeIndex represents how many "steps" we've moved
          const currentProgress = initialProgress + (activeIndex * productPercent);
          const progressPercent = Math.min(100, Math.max(0, currentProgress));
          progressBar.style.width = progressPercent + '%';
        }
      }

      function updateStatus(x) {
        // Clamp x for calculations
        const calcX = x > maxX ? maxX : (x < minX ? minX : x);
        
        if (x > maxX || x < minX) {
          return;
        }

        // Find closest snap point
        let closest = snapPoints[0];
        snapPoints.forEach(pt => {
          if (Math.abs(pt - calcX) < Math.abs(closest - calcX)) {
            closest = pt;
          }
        });
        activeIndex = snapPoints.indexOf(closest);
        
        // Update progress based on activeIndex
        updateProgress();

        // Update Slide Attributes
        items.forEach((slide,i) => {
          const r           = slide.getBoundingClientRect();
          const leftEdge    = r.left - collectionRect.left;
          const slideCenter = leftEdge + r.width/2;
          const inView      = slideCenter > 0 && slideCenter < collectionRect.width;
          const status      = i === activeIndex ? 'active' : inView ? 'inview' : 'not-active';

          slide.setAttribute('data-gsap-slider-item-status', status);
          slide.setAttribute('aria-selected',    i === activeIndex ? 'true' : 'false');
          slide.setAttribute('aria-hidden',      inView ? 'false' : 'true');
          slide.setAttribute('tabindex',         i === activeIndex ? '0'    : '-1');
        });

        // Update Controls
        controls.forEach(btn => {
          const dir = btn.getAttribute('data-gsap-slider-control');
          const can = dir === 'prev'
            ? activeIndex > 0
            : activeIndex < snapPoints.length - 1;

          btn.disabled = !can;
          btn.setAttribute('aria-disabled', can ? 'false' : 'true');
          btn.setAttribute('data-gsap-slider-control-status', can ? 'active' : 'not-active');
        });
      }

      // Remove old event listeners if they exist
      if (root._controlHandlers) {
        root._controlHandlers.forEach(({btn, handler}) => {
          btn.removeEventListener('click', handler);
        });
      }
      
      // Store handlers for cleanup
      root._controlHandlers = [];
      
      controls.forEach(btn => {
        const dir = btn.getAttribute('data-gsap-slider-control');
        const handler = () => {
          if (btn.disabled) return;
          const delta = dir === 'next' ? 1 : -1;
          const target = activeIndex + delta;
          // Update activeIndex immediately for progress calculation
          activeIndex = target;
          updateProgress();
          gsap.to(track, {
            duration: 0.4,
            x: snapPoints[target],
            onUpdate: function() {
              const currentX = gsap.getProperty(track,'x');
              updateStatus(currentX);
            }
          });
        };
        btn.addEventListener('click', handler);
        root._controlHandlers.push({btn, handler});
      });

      // Initialize Draggable
      root._sliderDraggable = Draggable.create(track, {
        type: 'x',
        inertia: true,
        bounds: {minX, maxX},
        throwResistance: 2000,
        dragResistance: 0.05,
        maxDuration: 0.6,
        minDuration: 0.2,
        edgeResistance: 0.75,
        snap: {x: snapPoints, duration: 0.4},
        onPress() {
          track.setAttribute('data-gsap-slider-list-status','grabbing');
          collectionRect = collection.getBoundingClientRect();
        },
        onDrag() {
          setX(this.x);
          updateStatus(this.x);
          updateProgress();
        },
        onThrowUpdate() {
          setX(this.x);
          updateStatus(this.x);
          updateProgress();
        },
        onThrowComplete() {
          setX(this.endX);
          updateStatus(this.endX);
          updateProgress();
          track.setAttribute('data-gsap-slider-list-status','grab');
        },
        onRelease() {
          setX(this.x);
          updateStatus(this.x);
          updateProgress();
          track.setAttribute('data-gsap-slider-list-status','grab');
        }
      })[0];

      // Initial state
      setX(0);
      activeIndex = 0;
      updateStatus(0);
      // Initialize progress bar with initial visible products
      updateProgress();
      
      // Mark as initialized
      root._sliderInitialized = true;
      root._needsReinit = false;
    });
  }

  // Debouncer: For resizing the window
  function debounceOnWidthChange(fn, ms) {
    let last = innerWidth, timer;
    return function(...args) {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (innerWidth !== last) {
          last = innerWidth;
          // Mark all sliders as needing reinit
          document.querySelectorAll('[data-gsap-slider-init]').forEach(root => {
            root._needsReinit = true;
          });
          fn.apply(this, args);
        }
      }, ms);
    };
  }

  window.addEventListener('resize', debounceOnWidthChange(initBasicGSAPSlider, 200));

  // Initialize Basic GSAP Slider
  function tryInit() {
    if (typeof gsap === 'undefined' || typeof Draggable === 'undefined' || typeof InertiaPlugin === 'undefined') {
      // Retry after a short delay if GSAP isn't loaded yet
      setTimeout(tryInit, 100);
      return;
    }
    initBasicGSAPSlider();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryInit);
  } else {
    tryInit();
  }
})();

// Testimonial Slider functionality
(function() {
  function initTestimonialSliders() {
    const testimonialContainers = document.querySelectorAll('[data-testimonial-slider]');
    
    if (testimonialContainers.length === 0) {
      return;
    }
    
    testimonialContainers.forEach((container, containerIndex) => {
      // Skip if already initialized
      if (container._testimonialSliderInitialized) {
        return;
      }
      
      // Mark as initialized
      container._testimonialSliderInitialized = true;
      
      const items = container.querySelectorAll('.testimonial-item');
      const dots = container.querySelectorAll('.testimonial-dot');
      const track = container.querySelector('.testimonials-track');
      
      if (items.length === 0 || !track) {
        return;
      }
      
      let currentIndex = 0;
      let autoplayInterval;

      // Find initial active index
      items.forEach((item, index) => {
        if (item.classList.contains('active')) {
          currentIndex = index;
        }
      });

      // Function to calculate and set track height based on tallest item
      function updateTrackHeight() {
        let maxHeight = 0;
        
        // Create a temporary clone container to measure heights without affecting the actual items
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.visibility = 'hidden';
        tempContainer.style.width = track.offsetWidth + 'px';
        document.body.appendChild(tempContainer);
        
        // Clone each item and measure its height
        items.forEach(item => {
          const clone = item.cloneNode(true);
          clone.style.position = 'relative';
          clone.style.opacity = '1';
          clone.style.visibility = 'visible';
          clone.style.top = 'auto';
          clone.style.left = 'auto';
          clone.classList.remove('active');
          tempContainer.appendChild(clone);
          
          const height = clone.offsetHeight;
          if (height > maxHeight) {
            maxHeight = height;
          }
        });
        
        // Clean up
        document.body.removeChild(tempContainer);
        
        // Set track height to the tallest item
        if (maxHeight > 0) {
          track.style.height = maxHeight + 'px';
        }
      }

      function showTestimonial(index) {
        // Remove active class from all items and dots
        items.forEach(item => item.classList.remove('active'));
        dots.forEach(dot => dot.classList.remove('active'));

        // Add active class to current item and dot
        if (items[index]) {
          items[index].classList.add('active');
        }
        if (dots[index]) {
          dots[index].classList.add('active');
        }

        currentIndex = index;
      }

      // Dot click handlers
      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          showTestimonial(index);
          resetAutoplay();
        });
      });

      // Auto-play functionality
      function nextTestimonial() {
        const nextIndex = (currentIndex + 1) % items.length;
        showTestimonial(nextIndex);
      }

      function resetAutoplay() {
        clearInterval(autoplayInterval);
        if (items.length > 1) {
          autoplayInterval = setInterval(nextTestimonial, 4000);
        }
      }

      // Start autoplay
      if (items.length > 1) {
        resetAutoplay();
      }

      // Pause on hover
      container.addEventListener('mouseenter', () => {
        clearInterval(autoplayInterval);
      });

      container.addEventListener('mouseleave', () => {
        if (items.length > 1) {
          resetAutoplay();
        }
      });

      // Initial height calculation
      updateTrackHeight();

      // Update height on window resize with debounce
      let resizeTimeout;
      function handleResize() {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          updateTrackHeight();
        }, 150);
      }

      window.addEventListener('resize', handleResize);
      
      // Store cleanup function
      container._testimonialSliderCleanup = () => {
        window.removeEventListener('resize', handleResize);
        clearInterval(autoplayInterval);
      };
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTestimonialSliders);
  } else {
    // If DOM is already loaded, run immediately
    initTestimonialSliders();
  }
  
  // Also try after a short delay in case elements are added dynamically
  setTimeout(initTestimonialSliders, 100);
})();

// Hide empty accordions (e.g. product metafield content that isn't populated)
(function() {
  function hideEmptyAccordions() {
    document.querySelectorAll('accordion-custom').forEach((accordion) => {
      const detailsContent = accordion.querySelector('.details-content');
      if (!detailsContent) return;

      const hasContent = detailsContent.textContent.trim().length > 0 ||
        detailsContent.querySelector('img, video, iframe, [src]');
      if (!hasContent) {
        accordion.style.display = 'none';
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hideEmptyAccordions);
  } else {
    hideEmptyAccordions();
  }
})();
